import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { camelize } from 'humps';
import { snakeCase } from 'lodash';
import moment from 'moment';

import { useDatabaseDescription } from '../../chats/providers';
import { useSources } from '../providers';
import { SchemasAccordionMenu, AnswerTable } from '../../chats/components';
import { AnimatedSpinner, H2, Text, Input, Icon, Avatar } from '../../../components';
import { VisibilityCellRenderer, VisibilityHeaderRenderer } from '../components/atoms';

// Add global styles for ag-grid cells to animate column width changes
const GlobalAgGridStyles = createGlobalStyle`
  .ag-cell,
  .ag-header-cell {
    transition: width 500ms ease;
  }
`;

const DataWarehouseContainer = styled.div`
  display: flex;
  height: 100%;
`;

const ViewDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 24px;
  max-height: 100%;
  overflow-y: scroll;
  border-left: 1px solid var(--border-muted);
`;

const SourceDescription = styled(Text)`
  margin-bottom: 32px;
`;

const FilterContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  position: relative;
`;

const FilterInput = styled(Input)`
  width: 400px;
  height: 40px;
  background: var(--wash-gray-default);
  padding: 4px 35px;
  border-radius: 8px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const DataWarehouseSearchInputIcon = styled(Icon)`
  position: absolute;
  top: 10px;
  left: 10px;
`;

const StyledAnimatedSpinner = styled(AnimatedSpinner)`
  height: 100%;
`;

const SchemaNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SourceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
`;

const LastSyncedAt = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 8px;
  background: var(--wash-gray-pressed);
  border-radius: 4px;
`;

const DataWarehouseAccordionMenu = styled(SchemasAccordionMenu)`
  padding-top: 24px;
  padding-left: 24px;
`;

const DataWarehouse = () => {
  // Local state for selected view (table) and schema.
  const [selectedView, setSelectedView] = useState(null);
  const [selectedSchema, setSelectedSchema] = useState(null);

  // Get full database description and sources.
  const { fullDatabaseDescription } = useDatabaseDescription();
  const { sources } = useSources();

  // Ref for the table/grid
  const gridRef = useRef(null);
  // Ref for the DataWarehouseContainer
  const containerRef = useRef(null);

  // Filter the database description to only include schemas that exist in sources.
  const filteredDatabaseDescription = useMemo(() => {
    if (!fullDatabaseDescription || !sources) return [];
    return fullDatabaseDescription.filter((schema) =>
      sources.find((source) => source.integration.schema === schema.name)
    );
  }, [fullDatabaseDescription, sources]);

  // Set a default selected view (table) when the filtered list changes.
  useEffect(() => {
    if (filteredDatabaseDescription.length > 0) {
      const firstSchemaViews = filteredDatabaseDescription[0]?.views;
      setSelectedView(Object.keys(firstSchemaViews)[0] || null);
    } else {
      setSelectedView(null);
    }
  }, [filteredDatabaseDescription]);

  // Derive the row data for the selected view.
  const rowData = useMemo(() => {
    if (!selectedView) return [];
    // Assume selectedView is in the format "schemaName.tableName"
    const viewSchemaName = selectedView.split('.')[0];
    const currentSchema = filteredDatabaseDescription.find(
      (schema) => camelize(schema.name) === viewSchemaName
    );
    if (!currentSchema) {
      return [];
    }
    const view = currentSchema.views[selectedView];
    if (!view || !view.columns) {
      return [];
    }
    return view.columns.map((item) => ({
      name: item.name,
      description: item.description,
      fillRate:
        item.fillRate != null
          ? `${Intl.NumberFormat('en-US', {
              maximumFractionDigits: 2,
              minimumFractionDigits: 0
            }).format(item.fillRate * 100)}%`
          : '',
      ignoreFromPrompts: item.ignoreFromPrompts,
      sampleValues: item.sampleValues?.slice(0, 3)
    }));
  }, [filteredDatabaseDescription, selectedView]);

  // Define column definitions for the view table.
  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Column Name',
        field: 'name',
        resizable: false,
        autoHeight: true,
        wrapText: true,
        width: 100,
        cellStyle: {
          display: 'flex',
          alignItems: 'flex-start',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }
      },
      {
        headerName: 'Description',
        field: 'description',
        resizable: false,
        autoHeight: true,
        wrapText: true,
        width: 200,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.8',
          padding: '7px 15px',
          display: 'flex',
          alignItems: 'flex-start'
        }
      },
      {
        headerName: 'Sample Values',
        field: 'sampleValues',
        resizable: false,
        autoHeight: true,
        wrapText: true,
        width: 100,
        cellStyle: {
          display: 'flex',
          alignItems: 'flex-start',
          lineHeight: '1.8',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }
      },
      {
        headerName: 'Fill rate',
        field: 'fillRate',
        autoHeight: true,
        wrapText: true,
        resizable: false,
        width: 50,
        cellStyle: {
          whiteSpace: 'normal',
          alignItems: 'center'
        }
      },
      {
        headerComponent: VisibilityHeaderRenderer,
        field: 'ignoreFromPrompts',
        cellRenderer: VisibilityCellRenderer,
        resizable: false,
        width: 50,
        cellStyle: {
          display: 'flex',
          alignItems: 'flex-start'
        }
      }
    ],
    []
  );

  // Column definitions when a schema is selected.
  const schemaColumnDef = useMemo(
    () => [
      {
        headerName: 'Table',
        field: 'table'
      },
      {
        headerName: 'Description',
        field: 'description',
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.8',
          padding: '15px'
        }
      }
    ],
    []
  );

  // Create row data for the schema details if a schema is selected.
  const schemaRowData = useMemo(() => {
    if (!selectedSchema) return [];
    const selectedDb = fullDatabaseDescription?.find((dbdesc) => dbdesc.name === selectedSchema);
    if (!selectedDb || !selectedDb.views) {
      return [];
    }
    const result = Object.values(selectedDb.views).map((view) => ({
      table: view.name.split('.')?.[1] || view.name,
      description: view.description
    }));
    return result;
  }, [fullDatabaseDescription, selectedSchema]);

  // Get the description for the selected schema.
  const schemaDescription = useMemo(() => {
    if (!selectedSchema) return '';
    const selectedDb = fullDatabaseDescription?.find((dbdesc) => dbdesc.name === selectedSchema);
    return selectedDb?.description || '';
  }, [fullDatabaseDescription, selectedSchema]);

  // Get the description for the source when a view is selected.
  const sourceDescription = useMemo(() => {
    if (!selectedView) return '';
    const viewSchemaName = selectedView.split('.')[0];
    const currentSchema = filteredDatabaseDescription.find(
      (dbdesc) => dbdesc.name === viewSchemaName
    );
    const view = currentSchema?.views?.[selectedView];
    return view?.description || currentSchema?.description || '';
  }, [filteredDatabaseDescription, selectedView]);

  // Memoized callback for filtering table rows.
  const onFilterTextBoxChanged = useCallback((e) => {
    if (gridRef.current?.api) {
      gridRef.current.api.setQuickFilter(e.target.value);
    }
  }, []);

  // Create a stable lookup object for sources to reduce callback recreations
  const sourcesLookup = useMemo(() => {
    if (!sources) return {};

    return sources.reduce((acc, source) => {
      acc[source.integration.schema] = {
        displayName: source.integration.displayName,
        pictureUrl: source.integration.pictureUrl,
        lastSync: source.lastSync
      };
      return acc;
    }, {});
  }, [sources]);

  const getSourceName = useCallback(
    (name) => sourcesLookup[name]?.displayName || name,
    [sourcesLookup]
  );

  const getSourcePicture = useCallback((name) => sourcesLookup[name]?.pictureUrl, [sourcesLookup]);

  const getLastSynced = useCallback((name) => sourcesLookup[name]?.lastSync, [sourcesLookup]);

  // Memoized schema name container to prevent unnecessary re-renders
  const schemaNameElement = useMemo(() => {
    if (!selectedSchema) return null;
    return (
      <SchemaNameContainer>
        <Avatar src={getSourcePicture(selectedSchema)} width={26} height={26} />
        {getSourceName(selectedSchema)}
      </SchemaNameContainer>
    );
  }, [selectedSchema, getSourceName, getSourcePicture]);

  // Memoized last synced element
  const lastSyncedElement = useMemo(() => {
    const schemaForSync = selectedSchema || selectedView?.split('.')[0];
    if (!schemaForSync) return null;

    return (
      <LastSyncedAt>
        <Text>
          Last synced {moment(getLastSynced(schemaForSync)).format('MMM D, YYYY [at] h:mm a')}
        </Text>
      </LastSyncedAt>
    );
  }, [selectedSchema, selectedView, getLastSynced]);

  // Show a spinner while data is loading.
  if (!filteredDatabaseDescription.length || !sources) {
    return <StyledAnimatedSpinner size={40} />;
  }

  return (
    <>
      {/* Include the global styles for ag-grid */}
      <GlobalAgGridStyles />
      <DataWarehouseContainer ref={containerRef}>
        <DataWarehouseAccordionMenu
          schemas={filteredDatabaseDescription}
          sources={sources}
          selectedTable={selectedView}
          setSelectedTable={setSelectedView}
          setSelectedSchema={setSelectedSchema}
          getSourceName={getSourceName}
          getSourcePicture={getSourcePicture}
          isSnakeCase
          withTableSearch
        />
        <ViewDetailsContainer>
          {(selectedSchema || selectedView) && (
            <>
              <SourceHeader>
                <H2>
                  {selectedSchema ? schemaNameElement : snakeCase(selectedView.split('.')[1])}
                </H2>
                {lastSyncedElement}
              </SourceHeader>
              <SourceDescription>
                {selectedSchema ? schemaDescription : sourceDescription}
              </SourceDescription>
              <FilterContainer>
                <DataWarehouseSearchInputIcon
                  name='search'
                  displaySize={20}
                  color='var(--content-subtitle)'
                />
                <FilterInput
                  type='text'
                  placeholder='Search for fields...'
                  onChange={onFilterTextBoxChanged}
                />
              </FilterContainer>
              <AnswerTable
                columnDefs={selectedSchema ? schemaColumnDef : columnDefs}
                rowData={selectedSchema ? schemaRowData : rowData}
                ref={gridRef}
                shouldShowAllRows
              />
            </>
          )}
        </ViewDetailsContainer>
      </DataWarehouseContainer>
    </>
  );
};

export { DataWarehouse };
